import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ClassicHtml } from '@/templates/classic/HtmlView';
import { ModernHtml } from '@/templates/modern/HtmlView';
import { CreativeHtml } from '@/templates/creative/HtmlView';
import { TemplatePreview } from '@/routes/home/TemplatePreview';
import { buildSampleCv, samplePhotoUrl } from '@/routes/home/sampleCv';

type TemplateId = 'classic' | 'modern' | 'creative';

const TEMPLATES: { id: TemplateId; color: string }[] = [
  { id: 'classic', color: '#1f2937' },
  { id: 'modern', color: '#0f766e' },
  { id: 'creative', color: '#7c3aed' },
];

export function TemplatesGallery() {
  const { t } = useTranslation();

  return (
    <section id="templates" className="relative border-t border-border">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-violet-50/40 to-transparent dark:via-violet-500/5"
      />
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
            {t('home.templates.eyebrow')}
          </p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('home.templates.title')}
          </h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">
            {t('home.templates.subtitle')}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {TEMPLATES.map(({ id, color }) => (
            <article
              key={id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="overflow-hidden border-b border-border bg-gradient-to-br from-slate-100 to-slate-200 p-4 dark:from-slate-800 dark:to-slate-900">
                <TemplatePreview>{renderTemplate(id, color)}</TemplatePreview>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-lg font-bold">
                    {t(`home.templates.${id}Name`)}
                  </h3>
                  <span
                    className="inline-block size-4 rounded-full shadow-sm ring-2 ring-white dark:ring-slate-800"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {t(`home.templates.${id}Desc`)}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Button asChild size="lg" variant="outline">
            <Link to="/editor">
              {t('home.templates.cta')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function renderTemplate(id: TemplateId, color: string) {
  const data = buildSampleCv(id, color);
  const props = {
    data,
    customColor: color,
    photoUrl: samplePhotoUrl,
    mode: 'preview' as const,
  };
  if (id === 'classic') return <ClassicHtml {...props} />;
  if (id === 'modern') return <ModernHtml {...props} />;
  return <CreativeHtml {...props} />;
}
